import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { atendimentoService } from '../../services/atendimentoService';

const defaultCustomSigameMsg = `🔀 *Ticket transferido para você!* 🚀

Olá *{{nome_usuario}}* 💫 
Um novo ticket foi transferido para você:

🎫*Ticket:* {{ticket_id}}
🙂 *Contato:* {{nome_contato}}

🗓️*Data do evento:* {{data_e_hora}}`;

const defaultGreetingTicketAcceptedText = `*{{ms}} {{name}}*, meu nome é *{{userName}}* e agora vou prosseguir com seu atendimento!`;

const defaultTransferTicketMsg = `Por favor, aguarde um momento, estou transferindo seu contato para o setor responsável para seguirmos com o seu atendimento. 🔄`;

const AtendimentoConfiguracoesTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [enviarMsgTransferencia, setEnviarMsgTransferencia] = useState(false);
  const [textoMsgTransferencia, setTextoMsgTransferencia] = useState('');
  const [enviarMsgAssumirTicket, setEnviarMsgAssumirTicket] = useState(false);
  const [textoMsgAssumirTicket, setTextoMsgAssumirTicket] = useState('');
  const [textoMsgSigame, setTextoMsgSigame] = useState('');
  const [enviarAudioComoArquivo, setEnviarAudioComoArquivo] = useState(false);

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      try {
        setLoading(true);
        const config = await atendimentoService.getConfiguracao();
        if (!ativo) return;

        setEnviarMsgTransferencia(!!config.enviar_msg_transferencia);
        setTextoMsgTransferencia(config.texto_msg_transferencia || '');
        setEnviarMsgAssumirTicket(!!config.enviar_msg_assumir_ticket);
        setTextoMsgAssumirTicket(config.texto_msg_assumir_ticket || '');
        setTextoMsgSigame(config.texto_msg_sigame || '');
        setEnviarAudioComoArquivo(!!config.enviar_audio_como_arquivo);
      } catch (err: any) {
        if (ativo) setErrorMsg(err.message || 'Erro ao carregar configuracoes do atendimento.');
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, []);

  const salvar = async () => {
    try {
      setSaving(true);
      setSuccessMsg('');
      setErrorMsg('');

      await atendimentoService.updateConfiguracao({
        enviar_msg_transferencia: enviarMsgTransferencia,
        texto_msg_transferencia: textoMsgTransferencia,
        enviar_msg_assumir_ticket: enviarMsgAssumirTicket,
        texto_msg_assumir_ticket: textoMsgAssumirTicket,
        texto_msg_sigame: textoMsgSigame,
        enviar_audio_como_arquivo: enviarAudioComoArquivo,
      });

      setSuccessMsg('Configuracoes do atendimento atualizadas com sucesso.');
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao salvar configuracoes do atendimento.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Configuracoes Gerais
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={enviarMsgTransferencia}
                onChange={(event) => setEnviarMsgTransferencia(event.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography fontWeight={500}>Enviar mensagem ao transferir ticket</Typography>
                <Typography variant="caption" color="text.secondary">
                  Envia automaticamente uma mensagem quando um ticket e transferido.
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={enviarMsgTransferencia}>
            <TextField
              value={textoMsgTransferencia}
              onChange={(event) => setTextoMsgTransferencia(event.target.value)}
              placeholder="Digite a mensagem que sera enviada ao transferir o ticket..."
              label="Mensagem de transferencia de ticket"
              size="small"
              fullWidth
              multiline
              minRows={4}
              helperText="Variaveis: {{firstName}}, {{name}}, {{ms}}, {{date}}, {{hour}}, {{date_hour}}, {{protocol}}."
            />
            {!textoMsgTransferencia && (
              <Button size="small" sx={{ mt: 1 }} onClick={() => setTextoMsgTransferencia(defaultTransferTicketMsg)}>
                Usar mensagem padrao
              </Button>
            )}
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={enviarMsgAssumirTicket}
                onChange={(event) => setEnviarMsgAssumirTicket(event.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography fontWeight={500}>Enviar mensagem quando ticket for assumido</Typography>
                <Typography variant="caption" color="text.secondary">
                  Envia automaticamente uma mensagem quando um atendente assume o ticket.
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={enviarMsgAssumirTicket}>
            <TextField
              value={textoMsgAssumirTicket}
              onChange={(event) => setTextoMsgAssumirTicket(event.target.value)}
              placeholder="Digite a mensagem que sera enviada quando o ticket for assumido..."
              label="Mensagem quando ticket e assumido"
              size="small"
              fullWidth
              multiline
              minRows={4}
              helperText="Variaveis: {{firstName}}, {{name}}, {{ms}}, {{date}}, {{hour}}, {{date_hour}}, {{protocol}}, {{userName}}."
            />
            {!textoMsgAssumirTicket && (
              <Button size="small" sx={{ mt: 1 }} onClick={() => setTextoMsgAssumirTicket(defaultGreetingTicketAcceptedText)}>
                Usar mensagem padrao
              </Button>
            )}
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 1 }}>
            Configuracoes do Siga-me
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Esta funcao envia uma notificacao para o WhatsApp do atendente quando ele estiver offline informando que o ticket foi transferido para ele.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            value={textoMsgSigame}
            onChange={(event) => setTextoMsgSigame(event.target.value)}
            placeholder="Digite a mensagem personalizada do Siga-me..."
            label="Mensagem do Siga-me"
            size="small"
            fullWidth
            multiline
            minRows={4}
            helperText="Variaveis: {{firstName}}, {{name}}, {{ms}}, {{date}}, {{hour}}, {{date_hour}}, {{protocol}}, {{ticket_id}}, {{userName}}."
          />
          {!textoMsgSigame && (
            <Button size="small" sx={{ mt: 1 }} onClick={() => setTextoMsgSigame(defaultCustomSigameMsg)}>
              Usar mensagem padrao
            </Button>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 1 }}>
            Opções de Mídia
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={enviarAudioComoArquivo}
                onChange={(event) => setEnviarAudioComoArquivo(event.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography fontWeight={500}>Enviar áudio como arquivo</Typography>
                <Typography variant="caption" color="text.secondary">
                  Se ativado, todo áudio gravado (ptt) será enviado como um arquivo de áudio comum.
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
            <Button variant="contained" size="small" disabled={saving} onClick={salvar}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AtendimentoConfiguracoesTab;
